import { difference, Polygon } from 'polygon-clipping';

export type ScreenRect = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};
export type VisibleMapGeometry = GeoJSON.MultiPolygon;
export const MAP_OCCLUDER_SELECTOR =
  '[data-map-occluder], .mapboxgl-control-container .mapboxgl-ctrl';

/** Convert a screen rectangle into a closed polygon for mask subtraction. */
const ring = (r: ScreenRect): Polygon => [
  [
    [r.left, r.top],
    [r.right, r.top],
    [r.right, r.bottom],
    [r.left, r.bottom],
    [r.left, r.top],
  ],
];

/** Point listings qualify by their geographic anchor, with a small clearance
 * from controls. Area interests qualify by exposed footprint. Do this before
 * server pagination, independently of marker collisions and loaded pages. */
export function visibleMapGeometry(
  viewport: ScreenRect,
  occluders: ScreenRect[],
  unproject: (point: [number, number]) => { lng: number; lat: number },
): VisibleMapGeometry {
  if (viewport.right <= viewport.left || viewport.bottom <= viewport.top) {
    return { type: 'MultiPolygon', coordinates: [] };
  }
  const masks = occluders
    .filter((r) => r.right > r.left && r.bottom > r.top)
    .map((r) =>
      ring({
        left: r.left - 4,
        top: r.top - 4,
        right: r.right + 4,
        bottom: r.bottom + 4,
      }),
    );
  const exposed = masks.length
    ? difference(ring(viewport), ...masks)
    : [ring(viewport)];
  return {
    type: 'MultiPolygon',
    coordinates: exposed.map((polygon) =>
      polygon.map((points) =>
        points.map((point) => {
          const { lng, lat } = unproject(point);
          return [Number(lng.toFixed(7)), Number(lat.toFixed(7))];
        }),
      ),
    ),
  };
}

/** Ignore controls hidden by their own styles or a hidden ancestor. */
export function isVisibleMapOccluder(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    const style = window.getComputedStyle(node);
    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.visibility === 'collapse' ||
      style.opacity === '0'
    )
      return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}
